/**
 * Sending an app invitation, one copy for web and mobile: the request, the
 * re-read and the reading of the answer. The dialog's state is
 * `invite-form.ts`; each platform draws it and the toast its own way. Mobile
 * imports these by subpath.
 *
 * The API answers three ways worth telling apart:
 * - 201 when it sent, and ALSO when the address already has an account. That
 *   is deliberate (the endpoint must not reveal who is registered), so the
 *   client reports both as sent and never tries to tell them apart.
 * - 400 for a spent quota and for an address someone already invited. Both
 *   carry only a message, so the client re-reads the user: no invitations left
 *   means the quota (another tab spent the last one), anything else means the
 *   address.
 * - 422 for an address the API's own validation refuses.
 *
 * The user is re-read after a send so `app_invitations_sent` stays the
 * server's count rather than a local guess.
 */

import { invitationsLeft } from './invitation-quota';
import { APP_INVITATION_COPY } from '../../config/app-invitation.config';
import { invitationService } from '../../services/invitation.service';
import { authStore } from '../../store/auth.store';
import { ApiErrorClass } from '../../types/api/errors';
import { emailSchema } from '../../validation/common.schemas';
import { getFieldErrorMessage } from '../error-messages';

import type { User } from '../../types/auth';

/** What a send came to, for the dialog to show. */
export type AppInvitationOutcome =
  | { kind: 'sent'; email: string }
  /** No invitations left: the dialog turns to its all-sent state. */
  | { kind: 'spent' }
  /** Belongs under the email field. */
  | { kind: 'field'; message: string }
  /** Not the address's fault: an alert, and the address stays. */
  | { kind: 'failed' };

/** What the send calls on; a test passes its own. */
export interface AppInvitationServices {
  invite: (email: string) => Promise<void>;
  /** Read the session user again; resolves whether the answer was taken. */
  readUser: () => Promise<boolean>;
  currentUser: () => Pick<User, 'app_invitations_sent'> | null;
}

const defaultServices: AppInvitationServices = {
  invite: (email) => invitationService.invite(email),
  readUser: () => authStore.getState().fetchUser(),
  currentUser: () => authStore.getState().user,
};

/** Send one invitation and say what became of it. Never throws. */
export async function sendAppInvitation(
  input: string,
  services: AppInvitationServices = defaultServices
): Promise<AppInvitationOutcome> {
  const email = input.trim();

  if (!emailSchema.safeParse(email).success) {
    return { kind: 'field', message: APP_INVITATION_COPY.invalidEmail };
  }

  try {
    await services.invite(email);
  } catch (error) {
    const status = error instanceof ApiErrorClass ? error.status : undefined;

    if (status === 422) {
      return {
        kind: 'field',
        message:
          getFieldErrorMessage(error, 'email') ??
          APP_INVITATION_COPY.invalidEmail,
      };
    }

    if (status === 400) {
      await services.readUser();
      return invitationsLeft(services.currentUser()) === 0
        ? { kind: 'spent' }
        : { kind: 'field', message: APP_INVITATION_COPY.alreadyInvited };
    }

    return { kind: 'failed' };
  }

  // The send stands whether or not the count could be read back.
  await services.readUser();
  return { kind: 'sent', email };
}
